import { type WelcomeMessageConfigDto, WelcomeMessagesService } from '@api'
import { attach, createEffect, createStore, sample } from 'effector'
import { matchPath } from 'react-router'

import { Pages } from '@/consts/routes.ts'
import { $creatorId } from '@/models/creators.ts'
import { initializeApp } from '@/models/init.ts'
import { $currentPage } from '@/models/router.ts'
import { toast } from '@/models/toasts.ts'
import { handleError } from '@/utils/error'

export const getWMSettingFx = attach({
  name: 'get welcome messages settings',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (_: void, { creatorId }) => ({
    creatorId: creatorId!,
  }),
  effect: createEffect(WelcomeMessagesService.getGlobalWelcomeMessageSettings),
})

getWMSettingFx.fail.watch(({ error, params }) => {
  handleError(error, 'Failed to get welcome messages settings', () =>
    getWMSettingFx(params),
  )
})

export const $WMSettings = createStore<WelcomeMessageConfigDto>(
  { state: false, minutes: 1 },
  { name: 'welcome messages settings' },
).on(getWMSettingFx.doneData, (_, data) => data)

export const updateWMSettingsFx = attach({
  name: 'update welcome messages settings',
  source: {
    settings: $WMSettings,
    creatorId: $creatorId,
  },
  mapParams: (
    requestBody: WelcomeMessageConfigDto,
    { creatorId, settings },
  ) => ({
    requestBody: {
      ...settings,
      ...requestBody,
    },
    creatorId: creatorId!,
  }),
  effect: createEffect(WelcomeMessagesService.updateSettings),
})

updateWMSettingsFx.doneData.watch(() => {
  toast.success('Settings updated successfully')
})

updateWMSettingsFx.failData.watch((error) => {
  handleError(error, 'Settings update failed')
  console.error('Settings update failed', error)
})

export const updateMinutesFx = (minutes: number) => {
  return updateWMSettingsFx({ minutes })
}

export const toggleStateFx = attach({
  name: 'toggle welcome messages state',
  source: {
    settings: $WMSettings,
  },
  mapParams: (_: any, { settings }) => ({
    state: !settings.state,
  }),
  effect: updateWMSettingsFx,
})

sample({
  clock: updateWMSettingsFx.doneData,
  target: $WMSettings,
})

sample({
  clock: [$currentPage, initializeApp, $creatorId],
  source: { page: $currentPage, creatorId: $creatorId },
  filter: ({ page, creatorId }) =>
    !!creatorId && !!matchPath(Pages.WELCOME_MESSAGES_OPTIONS, page),
  target: getWMSettingFx,
})
